import { z } from 'zod';
import { messageKey } from './messages.js';
/** Типы проверок (§7.5). */
export const monitorKindSchema = z.enum(['icmp', 'tcp', 'http', 'snmp']);
/** Состояние монитора. `unknown` — проверок ещё не было или порог не набран. */
export const monitorStatusSchema = z.enum(['up', 'down', 'unknown']);
const portSchema = z.int().min(1).max(65_535);
/**
 * Нижняя граница интервала — из критериев приёмки фазы 3: «интервал менее
 * 5 секунд отклоняется валидацией». Верхняя — сутки: больше суток проверка
 * перестаёт быть мониторингом.
 */
const intervalSecSchema = z.int().min(5).max(86_400);
/**
 * Предел ожидания не может быть больше интервала — иначе следующая проверка
 * начнётся раньше, чем закончится предыдущая. Соотношение проверяется ниже,
 * здесь только абсолютные границы.
 */
const timeoutMsSchema = z.int().min(100).max(60_000);
const thresholdSchema = z.int().min(1).max(10);
/**
 * Конфигурация HTTP-проверки. `expectStatus` перечислением, а не диапазоном:
 * «ожидаю 200 или 204» встречается, «ожидаю от 200 до 204» — нет.
 */
export const httpMonitorConfigSchema = z.object({
    path: z.string().startsWith('/').max(2000).default('/'),
    method: z.enum(['GET', 'HEAD', 'POST']).default('GET'),
    expectStatus: z.array(z.int().min(100).max(599)).min(1).max(10).default([200]),
    /** Подстрока, которая должна встретиться в теле ответа. */
    expectBody: z.string().max(500).nullable().default(null),
    followRedirects: z.boolean().default(false),
    tls: z.boolean().default(false),
});
/**
 * Конфигурация SNMP-проверки. Community — секрет уровня «сеть заказчика»,
 * но в версии 1 хранится в открытом виде: §7.6 заводит хранилище секретов
 * только для учётных данных VNC и камер. Отмечено в BACKLOG.
 */
export const snmpMonitorConfigSchema = z.object({
    oid: z
        .string()
        .regex(/^\.?\d+(\.\d+)+$/, messageKey('validation.monitor.oid'))
        .max(200),
    community: z.string().min(1).max(200).default('public'),
    version: z.enum(['1', '2c']).default('2c'),
    /** Ожидаемое значение; `null` — достаточно самого факта ответа. */
    expectValue: z.string().max(500).nullable().default(null),
});
export const emptyMonitorConfigSchema = z.object({});
/**
 * Разбор конфигурации по типу проверки (§11, фаза 3, задача 3).
 *
 * Union размечен полем `kind`, которое лежит в самом мониторе, а не внутри
 * `config`: в БД `config` — это JSONB без типа, и хранить тип дважды значит
 * однажды получить запись, где они разошлись.
 */
export function monitorConfigSchemaFor(kind) {
    switch (kind) {
        case 'http':
            return httpMonitorConfigSchema;
        case 'snmp':
            return snmpMonitorConfigSchema;
        case 'icmp':
        case 'tcp':
            return emptyMonitorConfigSchema;
    }
}
/** Проверки, которым порт обязателен, и те, которым он бессмысленен. */
export function portRequiredFor(kind) {
    return kind === 'tcp' || kind === 'http' || kind === 'snmp';
}
export const monitorSchema = z.object({
    id: z.uuid(),
    nodeId: z.uuid(),
    siteId: z.uuid(),
    /**
     * Дополнительный адрес узла, который проверяет этот монитор (ADR 036).
     * `null` — монитор самого узла; только такие определяют его состояние.
     */
    addressId: z.uuid().nullable(),
    kind: monitorKindSchema,
    target: z.string(),
    port: z.int().nullable(),
    config: z.record(z.string(), z.unknown()),
    intervalSec: z.int(),
    timeoutMs: z.int(),
    failThreshold: z.int(),
    okThreshold: z.int(),
    isEnabled: z.boolean(),
    currentStatus: monitorStatusSchema,
    currentSince: z.iso.datetime().nullable(),
    /**
     * Когда проверка выполнялась в последний раз. `null` — ни разу.
     *
     * Отличается от `currentSince`: тот говорит, когда статус стал таким,
     * а этот — что монитор вообще жив. Без него «up» получаса давности
     * неотличим от «up» секунду назад.
     */
    lastCheckedAt: z.iso.datetime().nullable(),
    lastRttMs: z.number().nullable(),
    lastError: z.string().nullable(),
    createdAt: z.iso.datetime(),
});
const monitorInputShape = {
    kind: monitorKindSchema,
    /** Пусто — берётся `nodes.address`. */
    target: z.string().trim().max(255).optional(),
    port: portSchema.nullable().optional(),
    config: z.record(z.string(), z.unknown()).optional(),
    intervalSec: intervalSecSchema.default(30),
    timeoutMs: timeoutMsSchema.default(2000),
    failThreshold: thresholdSchema.default(3),
    okThreshold: thresholdSchema.default(2),
    isEnabled: z.boolean().default(true),
};
/**
 * Предел ожидания должен помещаться в интервал. Иначе проверки наезжают друг
 * на друга: планировщик агента запустит следующую, пока висит предыдущая,
 * и на упавшем узле их накопится столько, сколько влезет в пул воркеров.
 */
function timeoutFitsInterval(value) {
    return value.timeoutMs <= value.intervalSec * 1000;
}
const TIMEOUT_ISSUE = {
    message: messageKey('validation.monitor.timeoutOverInterval'),
    path: ['timeoutMs'],
};
/**
 * Параметры проверки дополнительного адреса, когда наследовать их не от кого.
 *
 * Служебные мониторы адресов повторяют основной монитор узла (ADR 036), но
 * у узла может не быть ни одного — а наблюдать адреса всё равно надо. Числа
 * те же, что в `monitorInputShape`, и стоят рядом с ним намеренно: разъехавшись,
 * они дали бы адресам режим проверки, которого человек нигде не выбирал.
 */
export const DEFAULT_ADDRESS_CHECK = {
    kind: 'icmp',
    port: null,
    config: {},
    intervalSec: 30,
    timeoutMs: 2000,
    failThreshold: 3,
    okThreshold: 2,
    isEnabled: true,
};
export const createMonitorSchema = z
    .object(monitorInputShape)
    .refine(timeoutFitsInterval, TIMEOUT_ISSUE);
/**
 * Тип проверки не меняется: у каждого типа своя конфигурация, и смена типа
 * на месте оставила бы монитор с чужими параметрами. Проще удалить и создать.
 */
export const updateMonitorSchema = z
    .object(monitorInputShape)
    .omit({ kind: true })
    .partial()
    .refine((value) => value.intervalSec === undefined ||
    value.timeoutMs === undefined ||
    timeoutFitsInterval({ intervalSec: value.intervalSec, timeoutMs: value.timeoutMs }), TIMEOUT_ISSUE);
/** Разрешение истории (§8.2). `raw` — сырые точки, `5m` — непрерывный агрегат. */
export const historyResolutionSchema = z.enum(['raw', '5m']);
export const historyQuerySchema = z
    .object({
    from: z.iso.datetime(),
    to: z.iso.datetime(),
    /** Не задано — выбирается по длине периода. */
    resolution: historyResolutionSchema.optional(),
})
    .refine((value) => new Date(value.from) < new Date(value.to), {
    message: messageKey('validation.range.startBeforeEnd'),
    path: ['from'],
});
export const historyPointSchema = z.object({
    ts: z.iso.datetime(),
    okCount: z.int(),
    totalCount: z.int(),
    avgRttMs: z.number().nullable(),
    maxRttMs: z.number().nullable(),
});
export const historyResponseSchema = z.object({
    monitorId: z.uuid(),
    resolution: historyResolutionSchema,
    points: z.array(historyPointSchema),
});
/** Событие ленты площадки — смена состояния монитора (§7.5). */
export const monitorEventSchema = z.object({
    id: z.uuid(),
    monitorId: z.uuid(),
    nodeId: z.uuid(),
    nodeName: z.string(),
    monitorKind: monitorKindSchema,
    fromStatus: monitorStatusSchema,
    toStatus: monitorStatusSchema,
    at: z.iso.datetime(),
    error: z.string().nullable(),
});
export const eventsQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=monitors.js.map